import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import { ResponseArray, ResponseObject } from '@/types/shared'
import {
    AuthenticationResult,
    Department,
    Employee,
    Position,
} from '../types/response'
import { CreateEmployee, UpdateEmployeeAdvanced } from '../types/request'
import axios from 'axios'
import { FormValues } from '../components/Confirmation'
import { Option } from 'vnetwork-ui/dist/components/FormItem'
import { EmployeeDocument, ScanIdentityResponse } from '../types'
import { Attachment } from '@/modules/Upload/types'
import { DEPARTMENT_IDS } from '@/constants/departments'

export const getEmployees = async (
    payload: DataGridPayload
): Promise<ResponseArray<Employee>> => {
    const response = await datacentral.get(
        `/employees?${qs.stringify(payload)}`
    )
    return response.data
}

export interface GetEmployeesListParams {
    q?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    filters?: string // Pre-encoded filter query string
}

export const getEmployeesList = async (params: GetEmployeesListParams) => {
    const {
        page = 1,
        page_size = 20,
        q = '',
        sort_by,
        sort_order,
        filters,
    } = params

    const queryParams: Record<string, unknown> = { q, page, page_size }

    if (sort_by) {
        queryParams.sort_by = sort_by
        queryParams.sort_order = sort_order ?? 'desc'
    }

    if (filters) {
        queryParams.filters = filters
    }

    const response = await datacentral.get<ResponseArray<Employee>>(
        `/employees?${qs.stringify(queryParams)}`
    )
    if (!response || !response.data)
        return { results: [], pagination: undefined }
    return response.data
}

export const getEmployeesOptions = async (
    value: string,
    departmentIDs?: number[],
    status?: string
) => {
    const response = await datacentral.get<ResponseArray<Employee>>(
        `/employees/options?${qs.stringify({
            q: value,
            page: 1,
            page_size: 50,
            department_ids: departmentIDs,
            employee_status: status,
        })}`
    )

    if (!response?.data?.results) return []

    return response.data?.results?.map((e) => ({
        label: e?.employee_name,
        value: e?.employee_id,
    }))
}

/**
 * Paginated employee options for infinite scroll
 * Returns { options, hasMore, nextPage } format for AsyncFilterSelect
 */
export const getEmployeesOptionsPaginated = async (
    query: string,
    departmentIDs?: number[],
    status?: string,
    page: number = 1,
    pageSize: number = 5
) => {
    const response = await datacentral.get<ResponseArray<Employee>>(
        `/employees/options?${qs.stringify({
            q: query,
            page,
            page_size: pageSize,
            department_ids: departmentIDs,
            employee_status: status,
        })}`
    )

    if (!response?.data?.results) {
        return { options: [], hasMore: false }
    }

    const options = response.data.results.map((e) => ({
        label: e?.employee_name,
        value: e?.employee_id,
    }))

    const pagination = response.data.pagination
    // API returns page_next = -1 when no more pages
    const hasMore = pagination ? pagination.page_next !== -1 : false

    return {
        options,
        hasMore,
        nextPage: hasMore && pagination ? pagination.page_next : undefined,
    }
}

/** Resolve employee IDs to {label, value} options (for filter default values on reload) */
export const getEmployeesDefaultOptions = async (ids: (string | number)[]) => {
    if (!ids?.length) return []
    const numericIds = ids.map(Number).filter(Boolean)
    const employees = await getEmployeesByIds({ employee_ids: numericIds })
    return employees.map((e) => ({
        label: e?.employee_name,
        value: e?.employee_id,
    }))
}

/**
 * Paginated Account Manager options (employees from ACCOUNT department)
 * Returns { options, hasMore } format for AsyncFilterSelect
 */
export const getAccountManagerOptionsPaginated = async (
    query: string,
    page: number = 1,
    pageSize: number = 5
) => {
    const departmentIds = await getCsPoolDepartmentIds()
    const response = await datacentral.get<ResponseArray<Employee>>(
        `/employees/options?${qs.stringify({
            q: query,
            page,
            page_size: pageSize,
            department_ids: departmentIds,
            employee_status: 'active',
        })}`
    )

    if (!response?.data?.results) {
        return { options: [], hasMore: false }
    }

    const options = response.data.results.map((e) => ({
        label: e?.employee_name,
        value: e?.employee_id,
    }))

    const pagination = response.data.pagination
    // API returns page_next = -1 when no more pages
    const hasMore = pagination ? pagination.page_next !== -1 : false

    return {
        options,
        hasMore,
        nextPage: hasMore && pagination ? pagination.page_next : undefined,
    }
}

export const getAllDepartments = async (): Promise<
    ResponseArray<Department>
> => {
    const response = await datacentral.get('/departments/all')
    return response.data
}

/**
 * Department ids of the "account manager / CS" pool: BOTH the legacy "ACCOUNT"
 * (id=3) and the newer "Customer Service" (code CS) departments — a CS may be
 * assigned to either. Resolved by department_code so it stays correct across
 * environments (the CS dept id is auto-assigned per env, not a stable constant),
 * and memoized to a single departments fetch per session.
 */
let csPoolDepartmentIdsPromise: Promise<number[]> | null = null
export const getCsPoolDepartmentIds = (): Promise<number[]> => {
    if (!csPoolDepartmentIdsPromise) {
        csPoolDepartmentIdsPromise = getAllDepartments()
            .then((res) => {
                const csId = res?.results?.find(
                    (d) => d.department_code === 'CS'
                )?.department_id
                return csId
                    ? [DEPARTMENT_IDS.ACCOUNT, csId]
                    : [DEPARTMENT_IDS.ACCOUNT]
            })
            .catch(() => {
                // Allow a retry on the next call if the fetch failed.
                csPoolDepartmentIdsPromise = null
                return [DEPARTMENT_IDS.ACCOUNT]
            })
    }
    return csPoolDepartmentIdsPromise
}

export const getAllPositionsByDepartmentId = async (
    department_id: number
): Promise<ResponseArray<Position>> => {
    // /positions (paginated) reads department from the `filters` param and is page-limited;
    // /positions/department filters by the plain department_id and returns the full list.
    const response = await datacentral.get(
        `/positions/department?department_id=${department_id}`
    )
    return response.data
}

export const createEmployeeTmp = async (
    payload: CreateEmployee
): Promise<ResponseObject<{ token: string; employee: Employee }>> => {
    const response = await datacentral.post('/employees/', payload)
    return response.data
}

export const getCities = async (
    employee_country: string
): Promise<Option[]> => {
    const response = await datacentral.get(
        `/countries/states?country=${employee_country}`
    )
    return response.data?.data?.states || []
}

export const getDitricts = async (code: string): Promise<Option[]> => {
    const response = await datacentral.get(`/countries/districts?state=${code}`)
    return response.data?.data?.districts || []
}

export const getWards = async ({
    state,
    district,
}: {
    state: string
    district: string
}): Promise<Option[]> => {
    const response = await datacentral.get(
        `/countries/wards?state=${state}&district=${district}`
    )
    return response.data?.data?.wards || []
}

export const getEmployeeInfoByTokenConfirm = async (
    token: string
): Promise<ResponseObject<Employee>> => {
    const response = await axios.get(
        `${import.meta.env.APP_API_URL}/employees/info?token=${token}`
    )
    return response?.data
}

export const getEmployeeInfo = async (
    id: number
): Promise<ResponseObject<Employee>> => {
    const response = await datacentral.get(`/employees/${id}`)
    return response?.data
}

export const employeeConfirm = async (payload: {
    token: string
    params_employee: FormValues
}) => {
    const response = await axios.put(
        `${import.meta.env.APP_API_URL}/employees/`,
        payload
    )
    return response?.data
}

export const updateEmployee = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}`,
        payload.employee
    )
    return response?.data
}

export const updateEmployeeAdvanced = async (
    payload: UpdateEmployeeAdvanced
) => {
    const response = await datacentral.put(
        `/employees/update-day-off-left`,
        payload
    )
    return response?.data
}

export const disableEmployee = async (payload: {
    employee_id: number
    off_date?: string
}) => {
    const response = await datacentral.put(`/employees/disable`, payload)
    return response?.data
}

export const updateEmployeeProbationDate = async (payload: {
    employee_id: number
    probation_date: string
}) => {
    const response = await datacentral.post(
        `/employees/probation-date`,
        payload
    )
    return response?.data
}

export const getEmployeeContains = async (payload: {
    employee_ids: number[]
}) => {
    const response = await datacentral.post<ResponseArray<Employee>>(
        `/employees/contains`,
        payload
    )

    if (!response?.data?.results) return []

    return response?.data?.results?.map((e) => ({
        label: e?.employee_name,
        value: e?.employee_id,
        email: e?.employee_email_company,
        position_name: e?.position_name,
    }))
}

// Get full employee data by IDs (for preview selection)
export const getEmployeesByIds = async (payload: {
    employee_ids: number[]
}): Promise<Employee[]> => {
    const response = await datacentral.post<ResponseArray<Employee>>(
        `/employees/contains`,
        payload
    )
    return response?.data?.results || []
}

export const resendConfirmLink = async (payload: { employee_id: number }) => {
    const response = await datacentral.post(
        `/employees/${payload?.employee_id}/resend-confirmation`
    )
    return response?.data
}

export const activeAccountEmployee = async (payload: {
    employee_id: number
}): Promise<
    ResponseObject<{
        email: string
        password: string
        login_link: string
    }>
> => {
    const response = await datacentral.post(
        `/employees/${payload?.employee_id}/active`
    )

    return response?.data
}

// get employee by department ID
export const getEmployeeByDepartmentID = async (
    departmentID: number,
    q?: string,
    page?: number,
    page_size?: number
) => {
    const response = await datacentral.get(
        `/employees/departments?department_id=${departmentID}&q=${q || ''}&page=${
            page || 1
        }&page_size=${page_size || 5}`
    )
    if (!response?.data?.results) return []

    return response.data?.results?.map(
        (e: { employee_name: string; employee_id: number }) => ({
            label: e?.employee_name,
            value: e?.employee_id,
        })
    )
}

// scan identity from CCCD images
export const scanIdentity = async (payload: {
    employee_id: number
    attachments: {
        front: string // attachment URL for front side
        back: string // attachment URL for back side
    }
}): Promise<ResponseObject<any>> => {
    const response = await datacentral.post<
        ResponseObject<ScanIdentityResponse>
    >('/employees/identity', payload)
    return response.data
}

// get employee avatar
export const getEmployeeAvatar = async (id: number) => {
    const response = await datacentral.get<ResponseObject<string>>(
        `/employees/${id}/avatar`
    )
    return response.data?.result
}

// update employee avatar
export const updateEmployeeAvatar = async (payload: {
    employee_id: number
    avatar_url: string
}) => {
    const response = await datacentral.put(
        `/employees/${payload.employee_id}/avatar`,
        {
            avatar_url: payload.avatar_url,
        }
    )
    return response.data
}

export const updateEmployeeIdentification = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}/identification`,
        payload.employee
    )
    return response?.data
}

export const updateEmployeeAddress = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}/address`,
        payload.employee
    )
    return response?.data
}

export const updateEmployeeFinance = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}/financial`,
        payload.employee
    )
    return response?.data
}

export const updateEmployeeExperience = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}/education`,
        payload.employee
    )
    return response?.data
}

export const updateEmployeeAttendances = async (payload: {
    id: number
    employee: Partial<FormValues>
}): Promise<
    ResponseObject<{ authentication: AuthenticationResult; employee: Employee }>
> => {
    const response = await datacentral.put(
        `/employees/${payload.id}/attendances`,
        payload.employee
    )
    return response?.data
}

// get employee documents
export const getEmployeeDocuments = async (payload: { id: number }) => {
    const response = await datacentral.get<ResponseArray<EmployeeDocument>>(
        `/employees/${payload.id}/documents`
    )
    return response?.data?.results
}

// update employee documents
export const updateEmployeeDocuments = async (payload: {
    id: number
    documents: Partial<EmployeeDocument>[]
    remove_attachments: number[]
}) => {
    const response = await datacentral.put(
        `/employees/${payload.id}/documents`,
        {
            documents: payload.documents.map((document) => ({
                document_type: document.document_type,
                attachments: document.attachments as Attachment[],
            })),
            remove_attachments: payload.remove_attachments,
        }
    )
    return response?.data
}

// delete employee document attachment
export const deleteEmployeeDocument = async (payload: {
    id: number
    attachmentId: number
}) => {
    const response = await datacentral.delete(
        `/employees/${payload.id}/documents/attachments`,
        { data: { attachment_id: payload.attachmentId } }
    )
    return response?.data
}

// add employee document
export const addEmployeeDocument = async (payload: {
    id: number
    document_type: string
    attachments: Attachment[]
}) => {
    const response = await datacentral.post(
        `/employees/${payload.id}/documents`,
        {
            document_type: payload.document_type,
            attachments: payload.attachments,
        }
    )
    return response?.data
}

export const updateEmployeeDepartmentPosition = async (payload: {
    employee_id: number
    department_id?: number
    position_id?: number
}) => {
    const response = await datacentral.put(
        `/employees/${payload.employee_id}/department-position`,
        {
            department_id: payload.department_id,
            position_id: payload.position_id,
        }
    )
    return response?.data
}

// ==================== Department Services ====================

export interface GetDepartmentsListParams {
    q?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    filters?: string
}

export const getDepartmentsList = async (params: GetDepartmentsListParams) => {
    const {
        page = 1,
        page_size = 20,
        q = '',
        sort_by,
        sort_order,
        filters,
    } = params

    const queryParams: Record<string, unknown> = { q, page, page_size }

    if (sort_by) {
        queryParams.sort_by = sort_by
        queryParams.sort_order = sort_order ?? 'desc'
    }

    if (filters) {
        queryParams.filters = filters
    }

    const response = await datacentral.get<ResponseArray<Department>>(
        `/departments?${qs.stringify(queryParams)}`
    )
    if (!response || !response.data)
        return { results: [], pagination: undefined }
    return response.data
}

export const getDepartmentById = async (
    id: number
): Promise<ResponseObject<Department>> => {
    const response = await datacentral.get(`/departments/${id}`)
    return response.data
}

export const createDepartment = async (payload: {
    department_name: string
    department_code: string
    department_description?: string
    department_status?: string
    department_leader_id?: number
}): Promise<ResponseObject<Department>> => {
    const response = await datacentral.post('/departments/', {
        department: payload,
    })
    return response.data
}

export const updateDepartment = async (payload: {
    department_id: number
    department_name?: string
    department_code?: string
    department_description?: string
    department_status?: string
    department_leader_id?: number
}): Promise<ResponseObject<Department>> => {
    const { department_id, ...data } = payload
    const response = await datacentral.put(`/departments/${department_id}`, {
        department: data,
    })
    return response.data
}

export const deleteDepartment = async (
    department_id: number
): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete(`/departments/${department_id}`)
    return response.data
}

// ==================== POSITION SERVICES ====================

export const getPositionsList = async (
    params: Record<string, any>
): Promise<ResponseArray<Position>> => {
    const response = await datacentral.get('/positions', { params })
    return response.data
}

export const getPositionsByDepartment = async (
    department_id: number
): Promise<ResponseArray<Position>> => {
    const response = await datacentral.get('/positions/department', {
        params: { department_id },
    })
    return response.data
}

export const getPositionById = async (
    position_id: number
): Promise<ResponseObject<Position>> => {
    const response = await datacentral.get(`/positions/${position_id}`)
    return response.data
}

export const createPosition = async (payload: {
    position_name: string
    department_id: number
    position_is_executive?: boolean
}): Promise<ResponseObject<Position>> => {
    const response = await datacentral.post('/positions', { position: payload })
    return response.data
}

export const updatePosition = async (payload: {
    position_id: number
    position_name?: string
    department_id?: number
    position_is_executive?: boolean
}): Promise<ResponseObject<Position>> => {
    const { position_id, ...data } = payload
    const response = await datacentral.put(`/positions/${position_id}`, {
        position: data,
    })
    return response.data
}

export const deletePosition = async (
    position_id: number
): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete(`/positions/${position_id}`)
    return response.data
}
